/* eslint-disable import/no-named-as-default-member */
import Alpine from 'alpinejs'
import {
  fetchDeliveryZoneByZip,
  fetchFutureCampaigns,
} from '@/clients/pops/client'
import dayjs from 'dayjs'
import advancedFormat from 'dayjs/plugin/advancedFormat'
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter'
import weekOfYear from 'dayjs/plugin/weekOfYear'
import {
  getProgramStartDay,
  isProgramStartDateOOS,
  getDeliveryTimeString,
  getAvailableProgramStartDates,
  getDeliverySlot,
} from '@/scripts/program-panel/delivery-window'
import { getISO8601Date } from '@/scripts/program-panel/utilities'
import {
  UPDATE_ACTIVE_MEALS,
  UPDATE_DELIVERY_WINDOW,
  UPDATE_PROGRAM_LENGTH,
} from '@/constants/program-events'
import { HOLIDAY_WEEK, NEW_PROGRAM_WEEK_CAP } from '@/constants/program-dates'
import { ALL_MEALS } from '@/constants/programs'
import type { AvailableDate } from '@/types/components/program-panel/delivery-window'
import type { Campaign, DeliveryCycle } from '@/types/pops'
import { DeliveryZone, DeliveryZoneException } from '@/models/delivery-zone'

document.addEventListener('alpine:init', () => {
  dayjs.extend(weekOfYear)
  dayjs.extend(advancedFormat)
  dayjs.extend(isSameOrAfter)
  Alpine.data('deliveryWindow', () => ({
    zipCode: '',
    defaultZipCode: '10001',
    campaignId: undefined,
    campaignIdentifier: undefined,
    isHolidayOptInAllowed: undefined,
    programType: undefined,
    meals: ALL_MEALS,
    programLength: 1,
    daysOfMeal: undefined,
    futureCampaigns: undefined,
    deliveryZone: undefined,
    deliveryZoneCache: {},
    availableDates: [],
    oosDates: [],
    selectedDate: undefined,
    value: undefined,
    isSubscription: true,
    isZipCodeValid: false,
    isAppliedInputZipCode: false,
    isLoading: true,

    resetInputs() {
      this.value = undefined
      this.selectedDate = undefined
      this.availableDates = []
    },

    modifyZipCode() {
      this.isAppliedInputZipCode = false

      if (this.$refs.inputZipCode instanceof HTMLInputElement) {
        this.$refs.inputZipCode.focus()
        this.$refs.inputZipCode.select()
      }

      this.$dispatch(UPDATE_DELIVERY_WINDOW, {
        properties: this.getItemDeliveryProperties(),
        isZipCodeValid: false,
      })
    },

    applyZipCode() {
      this.isAppliedInputZipCode = true

      const isZipCodeValid = /^[0-9]{5}$/.test(this.zipCode)
      this.isZipCodeValid = isZipCodeValid
      if (!isZipCodeValid) {
        this.$dispatch(UPDATE_DELIVERY_WINDOW, {
          properties: this.getItemDeliveryProperties(),
          isZipCodeValid: false,
        })
        return
      }

      this.getAvailability()
    },

    formatDeliveryWindowDate(date: AvailableDate) {
      const { startDate, isOosDate, isException, exceptionDeliveries } = date
      const { week } = exceptionDeliveries[0] || {}

      return `${startDate.format('dddd, MMMM D')}${
        isOosDate ? ' (Sold Out)' : ''
      }${
        isException &&
        new DeliveryZone(this.deliveryZone).isScheduleChanged(
          this.daysOfMeal,
          dayjs(week),
        )
          ? '*'
          : ''
      }` // TODO: Remove hardcoding
    },

    getISO8601Date,

    exceptionNotificationText() {
      if (!this.selectedDate?.isException) return ''

      const {
        daysOfMealsDelivered,
        deliveries: holidayDeliveries,
        week,
      } = this.selectedDate.exceptionDeliveries[0]
      if (
        !new DeliveryZone(this.deliveryZone).isScheduleChanged(
          this.daysOfMeal,
          week,
        )
      )
        return ''

      const deliveryDay = holidayDeliveries
        .map((delivery: DeliveryCycle) => delivery.day)
        .join(' and ')

      const deliveryWeeks = this.selectedDate.exceptionDeliveries
        .map((exceptionDelivery: DeliveryZoneException) =>
          dayjs(exceptionDelivery.week).format('MMMM D'),
        )
        .join(' and ')
      const deliveryWeekText =
        this.programLength > 1 ? ` the week of ${deliveryWeeks}` : ''

      const paymentNotice =
        daysOfMealsDelivered < this.daysOfMeal
          ? 'Your payment will be adjusted accordingly for the shortened week.'
          : ''

      const message = `
        *Due to the ${
          this.selectedDate.startDate.isSame(HOLIDAY_WEEK, 'week')
            ? 'Thanksgiving holiday'
            : 'holiday schedule'
        }, you will receive ${daysOfMealsDelivered} days of meals arriving on ${deliveryDay}${deliveryWeekText}.
        ${paymentNotice}
      `

      return message
    },

    deliveryMessage() {
      const template = this.$el.dataset.messageTemplate
      if (!this.value || !template) return ''

      const selectedDate = (this.availableDates as AvailableDate[]).find(
        (date) => getISO8601Date(date.startDate) === this.value,
      )
      if (!selectedDate || !this.deliveryZone) return ''

      this.selectedDate = selectedDate

      const deliveryCount = String(selectedDate.deliveries.length)
      const deliveryDays = selectedDate.deliveries
        .map((delivery) => delivery.day)
        .join('+')
      const deliveryTime = getDeliveryTimeString(
        this.deliveryZone.default_delivery_window,
      )

      return template
        .replaceAll(
          '#DELIVERY_COUNT',
          deliveryCount === '1' ? '' : deliveryCount,
        )
        .replaceAll('#DELIVERY_WEEKDAYS', deliveryDays)
        .replaceAll('#DELIVERY_WINDOWS', deliveryTime)
        .replaceAll(
          '#SUBSCRIPTION_SUFFIX',
          this.isSubscription ? ' per week' : '',
        )
    },

    async getDeliveryZone(zipCode: string) {
      if (zipCode in this.deliveryZoneCache)
        return this.deliveryZoneCache[zipCode]
      return await fetchDeliveryZoneByZip(zipCode)
    },

    async getFutureCampaigns() {
      const lastMonth = dayjs()
        .startOf('month')
        .subtract(1, 'month')
        .format('YYYY-MM-DD')
      return await fetchFutureCampaigns(lastMonth)
    },

    async getAvailability() {
      this.isLoading = true
      const { zipCode, defaultZipCode } = this
      const requestZipCode = zipCode || defaultZipCode

      try {
        const deliveryZoneResponse = await this.getDeliveryZone(requestZipCode)
        this.deliveryZone = deliveryZoneResponse
        if (zipCode) {
          window.localStorage.setItem('sakara-zip', zipCode)
          this.isZipCodeValid = true
          this.isAppliedInputZipCode = true
        }
      } catch {
        this.isZipCodeValid = false
        this.isLoading = false
        return
      }

      Promise.all([this.futureCampaigns, this.deliveryZone]).then(
        ([futureCampaigns, deliveryZone]) => {
          this.deliveryZoneCache[requestZipCode] = deliveryZone
          this.futureCampaigns = futureCampaigns
          this.deliveryZone = deliveryZone
          this.isLoading = false
        },
      )
    },

    calculateDeliveries() {
      if (
        this.isLoading ||
        this.daysOfMeal === undefined ||
        !this.meals.length
      ) {
        this.resetInputs()
        return
      }

      const availableDates = []
      const {
        oosDates,
        daysOfMeal,
        meals,
        deliveryZone,
        programType,
        campaignId,
        campaignIdentifier,
        futureCampaigns,
      } = this
      let isAllowException = true
      let programLength = this.programLength

      if (this.programType === 'campaign' && !this.isHolidayOptInAllowed) {
        isAllowException = false
      }

      this.programLength = programLength

      const programStartRange = getAvailableProgramStartDates(
        NEW_PROGRAM_WEEK_CAP,
        programType,
        {
          campaignId,
          campaignIdentifier,
          futureCampaigns,
        },
      )

      const januaryDates = [
        '2025-01-06',
        '2025-01-13',
        '2025-01-20',
        '2025-01-27',
      ]

      const hideJanuaryDatesEnd = dayjs('2024-12-02')
      const today = dayjs()

      const currentPage = location.pathname.split('/')[2]
      const cyberWeekendPage = 'cyber-weekend-exclusive'

      for (let i = 0; i < programStartRange.length; i++) {
        let currentStartWeek = programStartRange[i]
        if (currentPage === cyberWeekendPage) {
          if (!januaryDates[i]) {
            continue
          }
          currentStartWeek = dayjs(januaryDates[i])
        }

        const deliverySlot = getDeliverySlot(
          programLength,
          meals,
          currentStartWeek,
          daysOfMeal,
          deliveryZone,
          isAllowException,
        )

        if (!deliverySlot) continue

        const { deliveries, isException, exceptionDeliveries } = deliverySlot

        const programStartDay = getProgramStartDay(deliveries)

        const programStartDate = currentStartWeek
          .day(programStartDay)
          .startOf('day')

        const isOosDate = isProgramStartDateOOS(oosDates, programStartDate)

        if (
          currentPage === cyberWeekendPage ||
          !(
            today.isSameOrBefore(hideJanuaryDatesEnd, 'day') &&
            januaryDates.includes(getISO8601Date(programStartDate))
          )
        ) {
          availableDates.push({
            startDate: programStartDate,
            deliveries,
            isOosDate,
            isException,
            exceptionDeliveries,
          })
        }
      }
      if (availableDates.length) {
        this.availableDates = availableDates

        const firstAvailableDate =
          availableDates.find((date) => !date.isOosDate) || availableDates[0]
        this.value = getISO8601Date(firstAvailableDate.startDate)
      }
    },

    getItemDeliveryProperties() {
      if (!this.selectedDate) return null

      const properties: Record<string, string | number> = {
        _zip_code: this.zipCode,
        _days_of_food: this.daysOfMeal.toString(),
        _delivery_zone_id: this.deliveryZone.id.toString(),
        _delivery_days_text: this.selectedDate.deliveries
          .map((delivery: DeliveryCycle) => delivery.day)
          .join(' and '),
        _delivery_window: this.deliveryZone.default_delivery_window.join('-'),
        _delivery_window_id:
          this.deliveryZone.default_delivery_window_id.toString(),
        _delivery_window_text: getDeliveryTimeString(
          this.deliveryZone.default_delivery_window,
        ),
        _meals: JSON.stringify([...this.meals]),
        _supplement: 'probiotics',
        _number_of_deliveries: String(this.selectedDate.deliveries.length),
        _start_date: this.value,
        _delivery_zone: this.deliveryZone.name,
        // _program_delivery_dates: ,  Add this when we have the data
        _kitchen: this.deliveryZone.kitchen,
      }

      if (this.programType === 'campaign' && this.campaignId) {
        const campaign = this.futureCampaigns.find(
          (campaign: Campaign) => campaign.id === this.campaignId,
        )
        properties._campaign_id = String(this.campaignId)
        if (campaign) properties._campaign_name = campaign.name
      }

      if (this.programType === 'campaign')
        properties._campaign_start_week = this.value

      return properties
    },

    init() {
      const {
        programType,
        campaignId,
        campaignIdentifier,
        programConfig: programConfigString,
      } = (document.querySelector('[x-data=programPanel]') as HTMLElement)
        .dataset
      this.programType = programType
      const programConfig = programConfigString
        ? JSON.parse(programConfigString)
        : {}
      const isHolidayOptInAllowed = programConfig.isHolidayOptInAllowed

      if (programType === 'campaign') {
        this.campaignId = campaignId ? Number(campaignId) : undefined
        this.futureCampaigns = this.getFutureCampaigns()
      }

      this.campaignIdentifier = campaignIdentifier
      this.isHolidayOptInAllowed = isHolidayOptInAllowed
      this.oosDates = (this.$root.dataset.oosDates || '').split(',')
      this.zipCode = window.localStorage.getItem('sakara-zip') || ''

      this.getAvailability()

      this.$watch('isLoading', () => {
        this.calculateDeliveries()
      })
      this.$watch('selectedDate', () => {
        this.$dispatch(UPDATE_DELIVERY_WINDOW, {
          properties: this.getItemDeliveryProperties(),
          isZipCodeValid: this.isZipCodeValid,
          selectedDate: this.selectedDate,
        })
      })

      this.$watch('programLength', () => {
        this.calculateDeliveries()
      })

      this.$watch('daysOfMeal', () => {
        this.calculateDeliveries()
      })
      this.$watch('meals', () => {
        this.calculateDeliveries()
      })
    },

    deliveryWindowRoot: {
      [`@${UPDATE_ACTIVE_MEALS}.window`]() {
        const { meals } = this.$event.detail
        this.meals = Array.isArray(meals) ? meals : [meals]
      },
      [`@${UPDATE_PROGRAM_LENGTH}.window`]() {
        const { programLength, programLengthTrial } = this.$event.detail
        this.daysOfMeal = Number(programLength)
        this.programLength = Number(programLengthTrial || 1)
      },
    },

    inputZipCodeWrapper: {
      [':class']() {
        return {
          'program-panel-delivery-window__input--error':
            !this.isZipCodeValid && this.isAppliedInputZipCode,
        }
      },
    },

    inputZipCodeAlert: {
      ':aria-hidden'() {
        return !(!this.isZipCodeValid && this.isAppliedInputZipCode)
      },
    },

    inputZipCode: {
      ['x-model']: 'zipCode',
      ['x-ref']: 'inputZipCode',
      ['@blur']() {
        this.applyZipCode()
      },
      ['@input.debounce.200ms']() {
        this.applyZipCode()
      },
      ['@keydown.enter.prevent']() {
        this.$el.blur()
        this.applyZipCode()
      },
    },

    ctaZipCode: {
      ['@click.prevent']() {
        if (this.isAppliedInputZipCode) {
          this.modifyZipCode()
        } else {
          this.applyZipCode()
        }
      },
      ['x-text']() {
        return this.isAppliedInputZipCode ? 'edit' : 'go'
      },
    },

    selectAvailableDate: {
      ['x-model']: 'value',
      [':disabled']() {
        return (
          this.isLoading || !this.isAppliedInputZipCode || !this.isZipCodeValid
        )
      },
    },

    shouldHideDeliveryMessage() {
      return (
        this.isLoading || !this.isAppliedInputZipCode || !this.isZipCodeValid
      )
    },

    exceptionNotification: {
      ['x-text']: 'exceptionNotificationText()',
      ['x-show']() {
        return !!this.exceptionNotificationText()
      },
    },

    textDeliveryMessage: {
      ['x-text']: 'deliveryMessage()',
      ['x-show']() {
        return !this.shouldHideDeliveryMessage()
      },
    },

    conditionalDisplay: {
      [':disabled']() {
        return (
          this.isLoading || !this.isAppliedInputZipCode || !this.isZipCodeValid
        )
      },
    },
  }))
})
